import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ShieldIcon from "@mui/icons-material/Shield";
import NatureIcon from "@mui/icons-material/Nature";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScienceIcon from "@mui/icons-material/Science";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import GroupsIcon from "@mui/icons-material/Groups";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import LanguageIcon from "@mui/icons-material/Language";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import CenterFocusWeakIcon from "@mui/icons-material/CenterFocusWeak";
import CloudIcon from "@mui/icons-material/Cloud";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import MenuIcon from "@mui/icons-material/Menu";
import MenuShelf from "./MenuShelf";
import * as THREE from "three";
import { Vector3, TextureLoader, LoadingManager } from "three";
import { cellRes, cellToLatLng, circleRadiusRad, dotSizeScale, latLngToCell, offsetLatLng } from "./fib-grid";
import { articlesOfPoints, collapseStorylines } from "./storylines";
import PuffLoader from "react-spinners/PuffLoader";
import { Globe } from "./Globe";
import { createGlowMesh } from "./glow-mesh";
import { createCloudMaterial } from "./cloud-shader";
import { CLOUD_IMAGE_VERSION, cloudHour, cloudMapUrl, createCloudMaps } from "./cloud-maps";
import { FRAME_HEIGHT, FRAME_WIDTH, createRecentClouds, paintMap } from "./recent-clouds";
import { applyEarthLighting } from "./earth-lighting";
import { sunView, updateLensFlare } from "./lens-flare";
import { meterEarth, targetExposure, createAdaptation } from "./exposure-meter";
import { monthBlend, seasonPath, createSeasonTextures } from "./seasons";
import { createDetailPatches } from "./detail-patches";
import { BASE_RES, cellScale, gridLevelFor, regionCells, regionKey } from "./grid-levels";
import { RING_MAX_PX, RING_MIN_PX, RING_RADII, RING_WIDTH_PX } from "./hex-wireframe";
import { createAtmosphereMaterial } from "./atmosphere-shader";
import { getKDTree } from "./nearest";
import HexCallout, { placeCallout, previewOpacity } from "./HexCallout";
import { getMoonSubPoint, createMoonMesh, MOON_RADIUS, MOON_SCENE_DIST } from "./moon";
import { MISSION_INTRO_MS, specialStoriesFor } from "./artemis-mission";
import { createMissionScene, framingDistance } from "./mission-scene";
import { createIssMarker, fetchIssTle } from "./iss";
import { getSunSubPoint, sceneTime, alignStarfield } from "./sun";
import { createSunMesh, SUN_RADIUS } from "./sun-mesh";
import { createTimelineCache, utcDayKey, WINDOW_MS, windowDays } from "./timeline-cache";
import { parsePublicationDate } from "./publication-time";
import { translatedFrom } from "./translation";

export const BACKEND_URL = process.env.REACT_APP_API_URL || "https://bluedot.onrender.com";
const CLOUDS_ALT = 0.002;

// One grid level drives both the picking bins and the wireframe geometry,
// so the visual dots line up exactly with the data points.
const HEX_RESOLUTION = BASE_RES; // from orbit; finer when zoomed in (grid-levels.js)
const GLOBAL_GRID = { res: HEX_RESOLUTION, cells: null, cellSet: null, key: "global" };
// Clouds fade out as the camera comes down (altitude in globe radii), so the
// detailed ground shows.
const CLOUD_FADE_FROM = 0.8;
const CLOUD_FADE_TO = 0.3;
const WIREFRAME_ALTITUDE = 0.005;
const WIREFRAME_OPACITY = 0.95;
const WIREFRAME_RIM_BOOST = 1.6;
const WIREFRAME_LINE_WIDTH = 3;
const WIREFRAME_POINT_SIZE = 36.0;

const CATEGORY_PALETTE = [
  "#ff6b6b", "#ffd93d", "#6bcb77", "#ff922b",
  "#cc5de8", "#20c997", "#f06595", "#74c0fc",
  "#a9e34b", "#ff8787", "#63e6be", "#4dabf7",
];

const CATEGORY_ICONS = {
  "Politics":      AccountBalanceIcon,
  "Conflict":      ShieldIcon,
  "Environment":   NatureIcon,
  "Economy":       TrendingUpIcon,
  "Science & Tech": ScienceIcon,
  "Health":        LocalHospitalIcon,
  "Society":       GroupsIcon,
};

// Smoothness of the altitude shell, in grid-spacing units.
//   0   → each grid dot sits at its own height (no blur; sharpest match to
//         the columns).
//   ~2  → each peak influences ~2 points around it.
//   ~6  → broad, blob-like swells.
// Tweak this constant to taste; runs once per popData update.
const WIREFRAME_SMOOTHNESS = 4.0;

// Where an expanded hex callout takes the camera: inside the regional grid
// level (grid-levels.js enters it below 0.6 / 1.12). The city-scale level
// (below ~0.2) is closer than the Earth imagery holds up.
const LOCAL_ALTITUDE = 0.45;
// Off for now: expanding keeps the camera where it is.
const ZOOM_ON_EXPAND = false;


// How much an article counts for at a given age (see the cell weights):
// (1 - x²)² of the fraction x of the 24-hour window it has aged. Nearly flat
// while news is fresh (so a few hours barely matter), falling faster as it
// ages, and reaching exactly 0 with zero slope as it leaves the window, so
// nothing steps. About 0.99 at 2 hours, 0.79 at 8, 0.56 at 12, 0.09 at 20.
export const recencyAt = (ageMs) => {
  const x = Math.min(1, Math.max(0, ageMs / WINDOW_MS));
  const left = 1 - x * x;
  return left * left;
};

// How strongly a story pulses at a given age: 1 when just published, easing
// to 0 at NEW_STORY_MS old, when it stops pulsing.
const NEW_STORY_MS = 3 * 3_600_000;
export const newnessAt = (ageMs) => {
  const left = Math.max(0, 1 - ageMs / NEW_STORY_MS);
  return left * left;
};

// Same curve the prisms use, so unsmoothed wireframe corners track prism tops.
const prismAltitude = (cell) =>
  cell.weight * (cell.maxImportance / 10) * 0.0001;

// Live cloud imagery from clouds.matteason.co.uk — updated every 3 hours,
// served as a 4K PNG with alpha transparency. Falls back silently on error.
const MATTEASON_CLOUDS_URL =
  "https://clouds.matteason.co.uk/images/4096x2048/clouds-alpha.png";

function fetchJsonWithTimeout(url, timeoutMs = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { signal: controller.signal })
    .then((response) => {
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return response.json();
    })
    .finally(() => clearTimeout(timer));
}

// A past day the API hasn't stored yet is rebuilt on request, which can take
// longer than one attempt waits; the rebuild continues server-side, so a
// retry usually gets the stored copy.
const DAY_FETCH_TIMEOUT_MS = 20000;
function fetchDayWithRetry(url) {
  return fetchJsonWithTimeout(url, DAY_FETCH_TIMEOUT_MS)
    .catch(() => fetchJsonWithTimeout(url, DAY_FETCH_TIMEOUT_MS));
}

const OPENING_ALTITUDE = 1.55;

// Opening camera altitude (globe radii above the surface) for a canvas
// aspect ratio: on portrait phones the desktop distance shows only a slice
// of ocean, so back off until the globe is about 110% of the screen width.
// Never closer than the desktop view.
export function openingAltitude(aspect, fovDeg = 57) {
  const halfWidthAngle = Math.atan(Math.tan((fovDeg * Math.PI) / 360) * aspect);
  return Math.max(OPENING_ALTITUDE, 1 / Math.sin(Math.min(Math.PI / 2, halfWidthAngle * 1.1)) - 1);
}
// Ocean roughness: the roughness map has the sea at 83/255 (land at 1), so
// the material's factor scales the sea to OCEAN_ROUGHNESS and land clamps
// at 1. 0.45 is a GGX match to Cox-Munk wave slopes for a ~7 m/s wind: the
// wide, soft sun glint seen from orbit.
const OCEAN_ROUGHNESS = 0.45;
const ROUGHNESS_MAP_OCEAN = 83 / 255;
const EARTH_ROUGHNESS = OCEAN_ROUGHNESS / ROUGHNESS_MAP_OCEAN;

// Constant daylight. It used to rise as the camera turned towards the night
// side (up to 8-16x), which can't light the night side but blew out the
// daylit edge near the terminator; city lights now handle the night side
// per pixel (earth-lighting.js).
const SUN_INTENSITY = 2.0;

// News setting: "None" in the categories hides the news layer (remembered).
function initialNewsShown() {
  try { return window.localStorage.getItem("news") !== "off"; } catch (_) { return true; }
}

// Clouds setting: the picker's toggle hides the cloud layer and its shadows
// (remembered).
function initialCloudsShown() {
  try { return window.localStorage.getItem("clouds") !== "off"; } catch (_) { return true; }
}
const CLOUD_SHADOW = 0.35;

// ISS setting: off unless the picker's toggle turned it on (remembered).
function initialIssShown() {
  try { return window.localStorage.getItem("iss") === "on"; } catch (_) { return false; }
}
const ISS_TLE_REFRESH_MS = 3 * 60 * 60 * 1000;

function setColourSpaces(textures) {
  for (const tex of textures) {
    if (!tex) continue;
    if (tex.colorSpace !== THREE.SRGBColorSpace) {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.needsUpdate = true;
    }
  }
}

// Per-frame brightness: fixed
// physical brightnesses (constant sun, faint stars, city lights at their own
// radiance) seen through one exposure, metered from the sunlit Earth on
// screen and adapting like an eye (exposure-meter.js).
const STAR_RADIANCE = 0.05;
const CITY_LIGHT_RADIANCE = 0.14;
const adaptExposure = createAdaptation();
// No ambient light: moonlight is ~1/400,000 of sunlight, so under the night
// exposure an ambient term lit the day texture, and bright deserts showed at
// midnight.
function applyBrightness(globe) {
  globe.sunLight.intensity = SUN_INTENSITY;
  const mat = globe.earthMesh?.material;
  if (mat && mat.emissiveIntensity !== undefined) mat.emissiveIntensity = CITY_LIGHT_RADIANCE;
  globe.skyboxMesh?.material.color.setScalar(STAR_RADIANCE);
  const sunDir = globe.sunLight.position.clone().normalize();
  globe.setExposure(adaptExposure(targetExposure(meterEarth(globe.camera(), globe.getGlobeRadius(), sunDir))));
}
// How long the globe stays covered waiting for the first data before it
// opens on the sub-solar point instead.
const OPENING_WAIT_MS = 5000;

// Eases the whole news layer's fade (Globe.setNewsFade) to `to`.
function fadeNews(globe, to, durationMs) {
  const from = globe.newsFade;
  const start = performance.now();
  let raf = null;
  const step = () => {
    const t = Math.min(1, (performance.now() - start) / durationMs);
    globe.setNewsFade(from + (to - from) * t * t * (3 - 2 * t));
    raf = t < 1 ? requestAnimationFrame(step) : null;
  };
  step();
  return { cancel: () => raf && cancelAnimationFrame(raf) };
}

// Centre of the tallest column (largest summed importance²), the same
// weight that sets column heights.
export function mostImportantSpot(cellsById) {
  let top = null;
  for (const cell of cellsById.values()) {
    if (!top || cell.weight > top.weight) top = cell;
  }
  if (!top) return null;
  const [lat, lng] = cellToLatLng(top.cellId);
  return { lat, lng };
}

function isSafeArticleLink(link) {
  try {
    return ["http:", "https:"].includes(new URL(link).protocol);
  } catch (_) {
    return false;
  }
}

// The live map from clouds.matteason.co.uk, a stand-in until the API's Live
// map arrives (e.g. while the API wakes up).
function loadCloudMap(url) {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      url,
      (tex) => {
        tex.colorSpace = THREE.NoColorSpace;
        resolve(tex);
      },
      undefined,
      reject
    );
  });
}

// How long the timeline must rest on a moment before its clouds are fetched,
// so scrubbing doesn't download a map for every step it passes.
const CLOUD_MAP_DELAY_MS = 300;
// How often the last day's scrubbing frames (and the Live hour) are checked.
const RECENT_CLOUDS_REFRESH_MS = 5 * 60 * 1000;
// How long swapping cloud maps (Live, scrubbing frames, a full map) fades,
// and a month-scale step from one day's clouds to the next.
const CLOUD_FADE_MS = 600;
const CLOUD_DAY_STEP_FADE_MS = 200;
const FULL_WIDTH = 4096;
const FULL_HEIGHT = 2048;

// A brightness image from the API (/clouds_frame, /clouds_full) as one byte
// per pixel, bottom row first to match a DataTexture.
async function loadBrightness(url, width, height) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: ${response.status}`);
  const bitmap = await createImageBitmap(await response.blob());
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.translate(0, height);
  ctx.scale(1, -1);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  const rgba = ctx.getImageData(0, 0, width, height).data;
  const out = new Uint8Array(width * height);
  for (let i = 0; i < out.length; i++) out[i] = rgba[i * 4];
  return out;
}

const loadCloudFrame = (hour) =>
  loadBrightness(`${BACKEND_URL}/clouds_frame?t=${hour}&v=${CLOUD_IMAGE_VERSION}`, FRAME_WIDTH, FRAME_HEIGHT);

// A cloud texture (grey in rgb and alpha per pixel, as the cloud shader reads
// them; RGBA because luminance+alpha isn't a valid WebGL2 texture format).
function cloudTexture(data, width, height, anisotropy) {
  const tex = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = anisotropy;
  tex.needsUpdate = true;
  return tex;
}

// A full-resolution map from /clouds_full as a cloud texture.
async function loadFullMap(url, anisotropy) {
  const brightness = await loadBrightness(url, FULL_WIDTH, FULL_HEIGHT);
  return cloudTexture(paintMap(new Uint8Array(FULL_WIDTH * FULL_HEIGHT * 4), brightness), FULL_WIDTH, FULL_HEIGHT, anisotropy);
}

// Earliest month with data (matches TIMELINE_START in timeline-time.js).
const FIRST_MONTH = "2023-10";
// How long the timeline thumb must rest on a day before its full article
// payload (titles, links, images) is fetched.
const FULL_DAY_FETCH_DELAY_MS = 300;

const timelineCache = createTimelineCache({
  fetchMonth: (month) => fetchJsonWithTimeout(`${BACKEND_URL}/timeline?month=${month}`, 20000),
});

// The month the window ends in (or the current month when live) plus its
// neighbours, most relevant first, so scrubbing either way lands on cached data.
function monthsAround(timeMs) {
  const end = new Date(timeMs == null ? Date.now() : timeMs - 1);
  const ym = (offset) => new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + offset, 1)).toISOString().slice(0, 7);
  const current = new Date().toISOString().slice(0, 7);
  return [ym(0), ym(-1), ym(1)].filter((m) => m >= FIRST_MONTH && m <= current);
}

function removeDuplicates(data) {
  const set = new Set();
  const out = [];
  for (const d of data) {
    const title = d.title.split("[")[0];
    if (!set.has(title)) {
      out.push(d);
      set.add(title);
    }
  }
  return out;
}

// Decodes HTML entities (&amp;, &#8217;, …) in feed text. A textarea's
// content is parsed as text, so markup stays literal and nothing runs.
let entityDecoder;
function decode(text) {
  if (!text) return "";
  entityDecoder ??= document.createElement("textarea");
  entityDecoder.innerHTML = text;
  return entityDecoder.value;
}

function toFullPoints(rawData) {
  return removeDuplicates(rawData).filter((d) =>
    Array.isArray(d.coordinates) && d.coordinates.length >= 2 &&
    Number.isFinite(d.coordinates[0]) && Number.isFinite(d.coordinates[1]) &&
    d.coordinates[0] >= -90 && d.coordinates[0] <= 90 &&
    d.coordinates[1] >= -180 && d.coordinates[1] <= 180 &&
    isSafeArticleLink(d.link)
  ).map((d) => ({
    article_id: d._id,
    lat: d.coordinates[0],
    lng: d.coordinates[1],
    val: d.importance || 5,
    title: decode(d.title),
    description: decode(d.description),
    image: d.image_url,
    date: d.pubDate,
    publishedAt: parsePublicationDate(d.pubDateUtc || d.pubDate).getTime(),
    link: d.link,
    source: d.link.split("/")[2],
    country: d.country,
    location: d.location,
    locationPrecision: d.location_precision,
    category: d.category,
    sentiment: d.sentiment,
    eventId: d.event_id || undefined,
    storylineId: d.storyline_id || undefined,
    outletCountry: d.outlet_country || undefined,
    translatedFrom: translatedFrom(d.language),
    originalTitle: d.title_original ? decode(d.title_original) : undefined,
  }));
}

const GlobeComponent = ({ onFocus, onHover = () => {}, onOutbound, onExpandStories, onStart, onData, onDataError, isMobile, time, retryKey, focusedStory, selectedCategory, onCategoryChange, selectedCountry, onCountryChange, mission = null, missionClock = null, onWatchMission }) => {
  const containerRef = useRef();
  // End of the timeline's rolling 24-hour window (ms since the epoch), or
  // null for live.
  const timeRef = useRef(time);
  timeRef.current = time;
  const globeEl = useRef();
  const atmosRef = useRef(null);
  const scatteringRef = useRef(null);
  const moonRef = useRef(null);          // { update, setSunDirection }
  const moonMeshRef = useRef(null);      // THREE.Mesh for raycast picking
  const issRef = useRef(null);           // createIssMarker handle
  const sunMeshRef = useRef(null);       // { update } — the visible Sun
  const viewModeRef = useRef("earth");   // "earth" | "moon"
  // Cloud maps (desktop only, like the live map): { show(texture), live,
  // maps } — `live` is the latest live texture, `maps` the timeline's hours.
  const cloudMapsRef = useRef(null);
  // Bumped when a scrubbing frame arrives, so the clouds repaint.
  const [cloudFramesVersion, setCloudFramesVersion] = useState(0);
  const recentCloudsTimer = useRef(null);

  const [popData, setPopData] = useState([]);
  const [allHexData, setAllHexData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [wireframeReady, setWireframeReady] = useState(false);

  const lastOpened = useRef(null);
  const interacted = useRef(false);
  // The opening view: the tallest column of the first data loaded. The globe
  // stays covered until then, so it opens already in place; whichever of the
  // camera setup and the first data comes second points the camera.
  const openingSpot = useRef(null);
  const cameraReady = useRef(false);
  const openingViewDone = useRef(false);
  const openingFallback = useRef(null);
  const openAtRef = useRef(null);
  const openingStoryPinned = useRef(false); // a linked story sets the opening view
  const [openingReady, setOpeningReady] = useState(false);
  const hoverD = useRef(null);
  const kdTree = useRef(null);
  const cellsById = useRef(new Map());
  const loaded = useRef(false);
  const isFirstDataLoad = useRef(true);
  const reportedData = useRef(null);
  const categoryColorsRef = useRef(new Map());

  const earthLightingRef = useRef(null);
  const seasonsRef = useRef(null);        // { show(date), upgrade(), cancel() }
  const updateDetailRef = useRef(null);   // per frame: zoomed-in detail patches
  // News grid level: global at HEX_RESOLUTION, or a finer region when zoomed.
  const [grid, setGrid] = useState(GLOBAL_GRID);
  const gridRef = useRef(grid);
  gridRef.current = grid;
  const colourTextures = useRef(new Set());

  const [newsShown, setNewsShownState] = useState(initialNewsShown);
  const newsShownRef = useRef(newsShown);
  newsShownRef.current = newsShown;
  const setNewsShown = (next) => {
    try { window.localStorage.setItem("news", next ? "on" : "off"); } catch (_) {}
    setNewsShownState(next);
  };

  const [cloudsShown, setCloudsShown] = useState(initialCloudsShown);
  const cloudsShownRef = useRef(cloudsShown);
  cloudsShownRef.current = cloudsShown;
  const cloudsMeshRef = useRef(null);
  const toggleClouds = () => {
    const next = !cloudsShownRef.current;
    try { window.localStorage.setItem("clouds", next ? "on" : "off"); } catch (_) {}
    setCloudsShown(next);
  };
  useEffect(() => {
    if (cloudsMeshRef.current) cloudsMeshRef.current.visible = cloudsShown;
    const lighting = earthLightingRef.current;
    if (lighting) lighting.uniforms.uCloudShadow.value = cloudsShown ? CLOUD_SHADOW : 0.0;
  }, [cloudsShown]);

  // The TLE is only fetched while the ISS is shown.
  const [issShown, setIssShown] = useState(initialIssShown);
  const toggleIss = () => {
    const next = !issShown;
    try { window.localStorage.setItem("iss", next ? "on" : "off"); } catch (_) {}
    setIssShown(next);
  };
  useEffect(() => {
    const iss = issRef.current;
    if (!iss || !issShown) return;
    iss.setVisible(true);
    const refresh = () => fetchIssTle(BACKEND_URL).then((satrec) => iss.setElements(satrec)).catch(() => {});
    refresh();
    const timer = setInterval(refresh, ISS_TLE_REFRESH_MS);
    return () => {
      clearInterval(timer);
      iss.setVisible(false);
    };
  }, [issShown, wireframeReady]);

  const [snapEnabled, setSnapEnabled] = useState(true);
  const snapEnabledRef = useRef(true);
  const toggleSnap = () => {
    const next = !snapEnabledRef.current;
    snapEnabledRef.current = next;
    setSnapEnabled(next);
  };

  const activeColor = selectedCategory
    ? (categoryColorsRef.current.get(selectedCategory) || "#4dd0ff")
    : "#4dd0ff";

  // Phones: the open hex's lead story floats over its column.
  const [calloutArticles, setCalloutArticles] = useState([]);
  const calloutRef = useRef(null);
  const updateCalloutRef = useRef(null); // per frame: place the callout

  const focus = (list) => {
    onFocus(list);
    setCalloutArticles(list);
  };

  // Expanded, the callout keeps the stories and place it opened with (zooming
  // in re-cuts the grid into finer hexes) and the altitude to return to.
  const [expandedStories, setExpandedStories] = useState(null);
  const expandRef = useRef(null); // { stories, pick, savedAlt }
  const expandCallout = () => {
    const globe = globeEl.current;
    const pick = hoverD.current;
    if (!globe || !pick) return;
    const alt = globe.camera().position.length() / globe.getGlobeRadius() - 1;
    const zoom = ZOOM_ON_EXPAND && alt > LOCAL_ALTITUDE * 1.05;
    expandRef.current = { stories: calloutArticles, pick: { ...pick }, savedAlt: zoom ? alt : null };
    setExpandedStories(calloutArticles);
    if (zoom) globe.zoomToAltitude(LOCAL_ALTITUDE);
    onExpandStories?.();
  };
  // `keepSelection`: the place stays open (the close button) rather than
  // being let go (a touch on the globe). `zoomBack`: return to the altitude
  // it opened from, unless the viewer is zooming themselves.
  const collapseCallout = (keepSelection, zoomBack) => {
    const open = expandRef.current;
    if (!open) return;
    expandRef.current = null;
    setExpandedStories(null);
    if (keepSelection) {
      hoverD.current = open.pick;
      lastOpened.current = open.pick;
      globeEl.current?.restoreSelection(globeEl.current.getCoords(open.pick.lat, open.pick.lng), open.pick.id);
      callbacksRef.current.onFocus(open.stories);
    }
    if (zoomBack && open.savedAlt != null && viewModeRef.current === "earth") {
      globeEl.current?.zoomToAltitude(open.savedAlt);
    }
  };
  const collapseRef = useRef(collapseCallout);
  collapseRef.current = collapseCallout;
  const callbacksRef = useRef({ onFocus: focus, onHover, onStart, onData, onDataError });
  useEffect(() => {
    callbacksRef.current = { onFocus: focus, onHover, onStart, onData, onDataError };
  });

  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });
  const globeWidth = isMobile ? dimensions.width : dimensions.width * 1.3;

  useEffect(() => {
    const resize = () => setDimensions({ height: window.innerHeight, width: window.innerWidth });
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Mount: build the globe, wire interactions, load textures, kick off initial camera fly-in.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let globe;
    try {
      globe = new Globe(container, {
        width: globeWidth,
        height: dimensions.height,
      });
    } catch (error) {
      setLoadError("The interactive globe is unavailable on this device.");
      setIsLoading(false);
      return;
    }
    globeEl.current = globe;

    const controls = globe.controls();
    const isDragging = { current: false };
    const isSnapping = { current: false };

    controls.addEventListener("start", () => {
      interacted.current = true;
      isDragging.current = true;
      // A wheel or pinch closes an expanded callout, leaving the zoom to them.
      collapseRef.current(true, false);
    });

    controls.addEventListener("change", () => {
      if (!loaded.current || !interacted.current) return;

      if (viewModeRef.current === "moon" && globe.sunLight && moonMeshRef.current) {
        // In moon view: camera orbits the moon — compute sun angle relative to moon
        const moonPos = moonMeshRef.current.position;
        const camDir = globe.camera().position.clone().sub(moonPos).normalize();
        const sunDir = globe.sunLight.position.clone().normalize();
        const sunDot = camDir.dot(sunDir); // 1 = facing sun, -1 = facing away
        const bgScale = (1.0 - sunDot) / 2.0;
        const intensity = 0.1 + (bgScale * 0.9);
        if (globe.skyboxMesh) {
          globe.skyboxMesh.material.color.setScalar(intensity);
        } else {
          globe.scene().backgroundIntensity = intensity;
        }
        return;
      }

      if (viewModeRef.current !== "earth") return;
      callbacksRef.current.onStart(false);

      if (globe.sunLight) applyBrightness(globe);

      if (isSnapping.current || !isDragging.current) return;

      // Find the hex nearest to the camera's foresight on the surface.
      const cameraPos = globe
        .camera()
        .position.clone()
        .normalize()
        .multiplyScalar(globe.getGlobeRadius());
      const geo = globe.toGeoCoords(cameraPos);
      
      // Weight the distance to the currently selected hex by 10x to make it 
      // easier to "break away" and switch to a neighbor while dragging.
      const nearestCandidates = kdTree.current?.nearest(geo, 5, [0.15 * cellScale(gridRef.current.res)]) || [];
      let closestHex = null;
      if (nearestCandidates.length) {
        const weighted = nearestCandidates.map(([hex, dist]) => {
          let wDist = dist;
          if (lastOpened.current && hex.id === lastOpened.current.id) {
            wDist *= 10.0;
          }
          return { hex, dist: wDist };
        });
        weighted.sort((a, b) => a.dist - b.dist);
        closestHex = weighted[0].hex;
      }

      const noSnap = !snapEnabledRef.current;
      if (closestHex) {
        if (!hoverD.current || closestHex.id !== hoverD.current.id) {
          hoverD.current = closestHex;
          globe.setHudCursor(globe.getCoords(closestHex.lat, closestHex.lng), closestHex.id, noSnap);

          const cell = cellsById.current.get(closestHex.id);
          if (cell) {
            callbacksRef.current.onFocus(articlesOfPoints(cell.points));
            callbacksRef.current.onHover(noSnap);
          }
        }
      } else if (hoverD.current) {
        lastOpened.current = null;
        globe.setHexHighlight(null, null, true);
        globe.setHudCursor(null);
        hoverD.current = null;
        callbacksRef.current.onFocus([]);
        callbacksRef.current.onHover(false);
      }
    });

    // Above the open hex's column: a faded preview of the hovered hex while
    // the globe moves, solid once it settles; hidden on the far side and in
    // moon view.
    const calloutAnchor = new THREE.Vector3();
    const calloutCorner = new THREE.Vector3();
    const toCamera = new THREE.Vector3();
    let calloutHex = null; // { id, centre, corner } — grid lookups once per hex
    let previewHex = null; // the hex whose preview is fading in, and since when
    let previewSince = 0;
    // The pointer's colour: .ui-panel's gradient runs rgb(0,149,255) on the
    // left to rgb(52,169,253) on the right.
    const gradientAt = (t) => `rgba(${Math.round(52 * t)}, ${Math.round(149 + 20 * t)}, ${Math.round(255 - 2 * t)}, 0.85)`;
    const screenOf = (v, camera, width, height) => {
      v.project(camera);
      return { x: (v.x + 1) / 2 * width, y: (1 - v.y) / 2 * height };
    };
    updateCalloutRef.current = () => {
      const el = calloutRef.current;
      if (!el) return;
      const open = expandRef.current;
      const pick = open ? open.pick : hoverD.current;
      // The place's hex at the grid level in use (it changes as the camera
      // zooms), empty or not.
      const id = pick && (cellsById.current.has(pick.id) ? pick.id : latLngToCell(pick.lat, pick.lng, gridRef.current.res));
      const cell = id && cellsById.current.get(id);
      let shown = !!pick && el.childElementCount > 0 && viewModeRef.current === "earth" && (!!open || !!cell);
      const preview = !open && (isDragging.current || isSnapping.current);
      if (shown) {
        if (calloutHex?.id !== id) {
          const centre = cellToLatLng(id);
          // A point on the dot's edge: its screen distance from the centre is
          // the callout's ring.
          calloutHex = { id, centre, corner: offsetLatLng(centre[0], centre[1], circleRadiusRad(cellRes(id)), 0) };
        }
        const camera = globe.camera();
        const alt = globe.haloAltitude(id) ?? 0;
        calloutAnchor.copy(globe.getCoords(calloutHex.centre[0], calloutHex.centre[1], alt));
        toCamera.copy(camera.position).sub(calloutAnchor);
        shown = toCamera.dot(calloutAnchor) > 0;
        if (shown) {
          const { clientWidth: width, clientHeight: height } = globe.renderer().domElement;
          calloutCorner.copy(globe.getCoords(calloutHex.corner[0], calloutHex.corner[1], alt));
          const centre = screenOf(calloutAnchor, camera, width, height);
          const corner = screenOf(calloutCorner, camera, width, height);
          // Below the menu button on phones (the category bar on desktop).
          const chips = (document.querySelector(".menu-button") || document.querySelector(".category-picker"))?.getBoundingClientRect();
          const timeline = document.querySelector(".timeline-panel")?.getBoundingClientRect();
          // Collapsed, the card takes its own size; expanded, it's sized here.
          const expanded = el.classList.contains("is-expanded");
          if (!expanded && el.style.width) el.style.width = el.style.height = "";
          // Expanded, the height its stories need: the last one's bottom
          // (offsetTop ignores the list's scroll) plus the list's padding.
          const lastStory = expanded && el.querySelector(".hex-callout__scroll")?.lastElementChild;
          const contentHeight = lastStory ? lastStory.offsetTop + lastStory.offsetHeight + 14 : Infinity;
          const place = placeCallout({
            x: centre.x,
            y: centre.y,
            // The selection ring's outer edge, as the halo shader sizes it.
            ring: Math.min(RING_MAX_PX, Math.max(RING_MIN_PX, RING_RADII * Math.hypot(corner.x - centre.x, corner.y - centre.y))) + RING_WIDTH_PX / 2,
            cardWidth: el.offsetWidth,
            cardHeight: el.offsetHeight,
            contentHeight,
            width,
            top: (chips ? chips.bottom : 132) + 8,
            bottom: (timeline ? timeline.top : height) - 8,
            expanded,
          });
          shown = !place.hidden;
          if (expanded) {
            const w = `${place.width}px`, h = `${Math.round(place.height)}px`;
            if (el.style.width !== w) el.style.width = w;
            if (el.style.height !== h) el.style.height = h;
          }
          el.style.transform = `translate(${Math.round(place.left)}px, ${Math.round(place.top)}px)`;
          el.style.setProperty("--pointer", `${Math.round(place.pointer)}px`);
          el.style.setProperty("--pointer-color", gradientAt(place.pointer / el.offsetWidth));
          el.classList.toggle("is-below", place.below);
        }
      }
      el.classList.toggle("is-shown", shown);
      el.classList.toggle("is-preview", shown && preview);
      // Previews fade from nothing for each new hex; once settled, the
      // stylesheet's transition takes the card the rest of the way.
      if (shown && preview) {
        if (previewHex !== id) {
          previewHex = id;
          previewSince = performance.now();
        }
        el.style.opacity = previewOpacity(performance.now() - previewSince).toFixed(3);
      } else {
        if (!shown) previewHex = null;
        if (el.style.opacity) el.style.opacity = "";
      }
    };

    controls.addEventListener("end", () => {
      isDragging.current = false;
      if (!loaded.current) return;
      if (viewModeRef.current !== "earth") return; // no snap in moon view

      if (!hoverD.current) {
        lastOpened.current = null;
        globe.setHexHighlight(null, null, true);
        callbacksRef.current.onFocus([]);
        callbacksRef.current.onHover(false);
        return;
      }

      if (snapEnabledRef.current) {
        isSnapping.current = true;
        setTimeout(() => { isSnapping.current = false; }, 300);
        globe.pointOfView(
          { lat: hoverD.current.lat, lng: hoverD.current.lng },
          300
        );
      }
      globe.setHudCursor(globe.getCoords(hoverD.current.lat, hoverD.current.lng), hoverD.current.id, true);
      if (hoverD.current !== lastOpened.current) {
        callbacksRef.current.onHover(true);
        const cell = cellsById.current.get(hoverD.current.id);
        if (cell) {
          const alt = prismAltitude(cell);
          const worldPos = globe.getCoords(hoverD.current.lat, hoverD.current.lng, alt);
          globe.triggerWave(worldPos);
        }
      }
      lastOpened.current = hoverD.current;
    });

    if (!interacted.current) callbacksRef.current.onStart(true);

    // Raycast-based hex picker for pointer events.
    const canvas = globe.renderer().domElement;
    let pointerDownPos = null;

    const raycastHex = (e) => {
      if (!kdTree.current) return null;
      const rect = canvas.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, globe.camera());
      // Pick against the underlying earth sphere, not the displaced density mesh —
      // the visual surface puffs up at peaks but interaction tracks geographic position.
      const intersects = raycaster.intersectObject(globe.earthMesh, false);
      if (!intersects.length) return null;
      const { lat, lng } = globe.toGeoCoords(intersects[0].point);
      const cellId = latLngToCell(lat, lng, gridRef.current.res);
      if (cellsById.current.get(cellId)) return { lat, lng, id: cellId };
      const nearest = kdTree.current.nearest({ lat, lng }, 1, [0.04 * cellScale(gridRef.current.res)]);
      return nearest.length ? nearest[0][0] : null;
    };

    const onPointerDown = (e) => {
      pointerDownPos = { x: e.clientX, y: e.clientY };
      collapseRef.current(false, true);
      if (!loaded.current || viewModeRef.current !== "earth") return;
      if (hoverD.current) {
        lastOpened.current = null;
        hoverD.current = null;
        globe.setHudCursor(null);
        globe.setHexHighlight(null, null, true);
        callbacksRef.current.onFocus([]);
        callbacksRef.current.onHover(false);
      }
    };

    const onPointerUp = (e) => {
      if (!pointerDownPos || !loaded.current) return;
      const dx = e.clientX - pointerDownPos.x;
      const dy = e.clientY - pointerDownPos.y;
      if (Math.sqrt(dx * dx + dy * dy) > 5) return;

      // Secret feature: click the moon to fly there, click Earth to fly back.
      const rect = canvas.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, globe.camera());

      if (viewModeRef.current === "earth" && moonMeshRef.current) {
        const moonHits = raycaster.intersectObject(moonMeshRef.current, false);
        if (moonHits.length) {
          viewModeRef.current = "moon";
          moonRef.current?.upgradeTextures();
          globe.flyToObject(moonMeshRef.current.position, MOON_RADIUS, 2000, (e) => {
            // The news net shrinks away over the first half of the flight.
            globe.setNewsFade(1 - 2 * e);
            if (moonRef.current) {
              moonRef.current.setBrightness(4.0 + (1.1 - 4.0) * e);
            }
            if (globe.sunLight && moonMeshRef.current && globe.skyboxMesh) {
              const camPos  = globe.camera().position;
              const sunDir  = globe.sunLight.position.clone().normalize();
              const earthDot = camPos.clone().normalize().dot(sunDir);
              const moonDot  = camPos.clone().sub(moonMeshRef.current.position).normalize().dot(sunDir);
              const earthI = 0.1 + ((1.0 - earthDot) / 2.0) * 0.9;
              const moonI  = 0.1 + ((1.0 - moonDot)  / 2.0) * 0.9;
              globe.skyboxMesh.material.color.setScalar(earthI * (1 - e) + moonI * e);
            }
          });
          return;
        }
      } else if (viewModeRef.current === "moon" && globe.earthMesh) {
        const earthHits = raycaster.intersectObject(globe.earthMesh, false);
        if (earthHits.length) {
          viewModeRef.current = "earth";
          globe.flyBackToEarth(MOON_RADIUS, 2000, (e) => {
            // And grows back over the second half, as Earth comes close.
            globe.setNewsFade(2 * e - 1);
            if (moonRef.current) {
              moonRef.current.setBrightness(1.1 + (4.0 - 1.1) * e);
            }
            if (globe.sunLight && moonMeshRef.current && globe.skyboxMesh) {
              const camPos  = globe.camera().position;
              const sunDir  = globe.sunLight.position.clone().normalize();
              const moonDot  = camPos.clone().sub(moonMeshRef.current.position).normalize().dot(sunDir);
              const earthDot = camPos.clone().normalize().dot(sunDir);
              const moonI  = 0.1 + ((1.0 - moonDot)  / 2.0) * 0.9;
              const earthI = 0.1 + ((1.0 - earthDot) / 2.0) * 0.9;
              globe.skyboxMesh.material.color.setScalar(moonI * (1 - e) + earthI * e);
            }
          });
          return;
        }
      }

      // Normal hex-picking behaviour (only in Earth mode).
      if (viewModeRef.current !== "earth") return;

      if (!snapEnabledRef.current) {
        // No-snap mode: selection already happened via hover; just trigger the wave on release.
        if (hoverD.current) {
          const cell = cellsById.current.get(hoverD.current.id);
          if (cell) {
            const alt = prismAltitude(cell);
            const worldPos = globe.getCoords(hoverD.current.lat, hoverD.current.lng, alt);
            globe.triggerWave(worldPos);
          }
          lastOpened.current = hoverD.current;
        }
        return;
      }

      const closestHex = raycastHex(e);
      if (!closestHex) {
        lastOpened.current = null;
        globe.setHexHighlight(null, null, true);
        callbacksRef.current.onFocus([]);
        callbacksRef.current.onHover(false);
        return;
      }

      interacted.current = true;
      callbacksRef.current.onStart(false);
      hoverD.current = closestHex;
      globe.setHudCursor(globe.getCoords(closestHex.lat, closestHex.lng), closestHex.id, true);

      const cell = cellsById.current.get(closestHex.id);
      if (cell) callbacksRef.current.onFocus(articlesOfPoints(cell.points));

      isSnapping.current = true;
      setTimeout(() => { isSnapping.current = false; }, 300);
      globe.pointOfView(
        { lat: closestHex.lat, lng: closestHex.lng },
        300
      );
      if (closestHex !== lastOpened.current) {
        callbacksRef.current.onHover(true);
        const cell2 = cellsById.current.get(closestHex.id);
        if (cell2) {
          const alt = prismAltitude(cell2);
          const worldPos = globe.getCoords(closestHex.lat, closestHex.lng, alt);
          globe.triggerWave(worldPos);
        }
      }
      lastOpened.current = closestHex;
    };

    const onPointerMove = (e) => {
      if (!loaded.current || isDragging.current) return;
      if (viewModeRef.current !== "earth") return;
      const hex = raycastHex(e);
      canvas.style.cursor = hex ? "pointer" : "default";
      if (!snapEnabledRef.current) {
        if (hex) {
          if (!hoverD.current || hex.id !== hoverD.current.id) {
            hoverD.current = hex;
            globe.setHudCursor(globe.getCoords(hex.lat, hex.lng), hex.id, true);
            const cell = cellsById.current.get(hex.id);
            if (cell) {
              callbacksRef.current.onFocus(articlesOfPoints(cell.points));
              callbacksRef.current.onHover(true);
            }
          }
        } else if (hoverD.current) {
          lastOpened.current = null;
          hoverD.current = null;
          globe.setHudCursor(null);
          callbacksRef.current.onFocus([]);
          callbacksRef.current.onHover(false);
        }
      }
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointermove", onPointerMove);

    // Load textures, fetch initial sun position, apply materials + atmosphere,
    // and start the GIBS live-clouds refresh cycle.
    const smallScreen = window.innerWidth <= 767;
    const openingSeason = monthBlend(sceneTime(timeRef.current));
    const texturePaths = [
      seasonPath(openingSeason.lower, "2k"),
      "/topology2k.jpg",
      "/water2k.jpg",
      "/roughness2k.jpg",
      "/night2k.jpg",
      "/clouds3.jpg",
      "/starmap2k.jpg",
    ];
    const manager = new LoadingManager();
    const loader = new TextureLoader(manager);
    const texturePromise = new Promise((resolve) => {
      let timeout = setTimeout(() => resolve(textures), 8000);
      manager.onLoad = () => {
        clearTimeout(timeout);
        resolve(textures);
      };
      const textures = texturePaths.map((f) => loader.load(f));
    });

    texturePromise.then((textures) => {
      const [earthMap, topo, water, rough, night, cloudsTex, starmapTex] = textures;
      // Sharper surfaces at glancing angles towards the limb.
      const maxAnisotropy = globe.renderer().capabilities.getMaxAnisotropy();
      for (const tex of [earthMap, topo, water, rough, night, cloudsTex]) tex.anisotropy = maxAnisotropy;
      // Colour textures are sRGB; data textures (bump, water, roughness,
      // cloud cover) stay linear.
      colourTextures.current = new Set([earthMap, night, starmapTex]);
      setColourSpaces(colourTextures.current);

      const initialTime = sceneTime(timeRef.current);
      const sunSub = getSunSubPoint(initialTime);
      const sunpos = globe.getCoords(sunSub.lat, sunSub.lng);
      globe.sunLight.position.copy(sunpos);

      globe.earthMesh.material.dispose();
      globe.earthMesh.material = new THREE.MeshPhysicalMaterial({
        map: earthMap,
        roughness: EARTH_ROUGHNESS,
        color: 0xffffff,
        bumpMap: topo,
        bumpScale: 0.1,
        specularIntensityMap: water,
        roughnessMap: rough,
        specularIntensity: 1.0,
        emissiveMap: night,
        emissive: 0xffffff,
        emissiveIntensity: 0.2,
      });
      const earthLighting = applyEarthLighting(globe.earthMesh.material, { clouds: cloudsTex, water, night, earthMesh: globe.earthMesh, cloudAltitude: CLOUDS_ALT });
      earthLighting.setSunIrradiance(SUN_INTENSITY);
      earthLighting.uniforms.uCityRadiance.value = CITY_LIGHT_RADIANCE;
      earthLightingRef.current = earthLighting;

      // Seasonal colour follows the selected moment. Both months must be
      // loaded before a change shows; loads wait for the timeline to rest.
      const earthMaterialRef = () => globe.earthMesh.material;
      const seasonTextures = createSeasonTextures({
        load: (path) => new TextureLoader().loadAsync(path),
        prepare: (texture) => {
          texture.anisotropy = maxAnisotropy;
          colourTextures.current.add(texture);
          setColourSpaces([texture]);
        },
        release: (texture) => {
          if (texture === earthMaterialRef().map || texture === earthLighting.uniforms.uSeasonNext.value) return;
          colourTextures.current.delete(texture);
          texture.dispose();
        },
      });
      seasonTextures.put(openingSeason.lower, "2k", earthMap);
      let seasonRes = "2k";
      let seasonRequest = 0;
      let seasonTimer = null;
      const showSeason = (date) => {
        const { lower, upper, t } = monthBlend(date);
        const res = seasonRes;
        const request = ++seasonRequest;
        const apply = async () => {
          const [a, b] = await Promise.all([seasonTextures.get(lower, res), seasonTextures.get(upper, res)]);
          if (request !== seasonRequest || globeEl.current !== globe) return;
          earthMaterialRef().map = a;
          earthLighting.uniforms.uSeasonNext.value = b;
          earthLighting.uniforms.uSeasonBlend.value = t;
        };
        clearTimeout(seasonTimer);
        if (seasonTextures.has(lower, res) && seasonTextures.has(upper, res)) {
          apply().catch(() => {});
        } else {
          seasonTimer = setTimeout(() => apply().catch(() => {}), 300);
        }
      };
      seasonsRef.current = {
        show: showSeason,
        upgrade: () => {
          seasonRes = "4k";
          showSeason(sceneTime(timeRef.current));

      // Zoomed-in detail: NASA tiles around the view centre, rebuilt once
      // the camera has rested.
      const detailUniforms = earthLighting.uniforms;
      const detailPatches = createDetailPatches({
        prepare: (texture) => {
          texture.anisotropy = maxAnisotropy;
          colourTextures.current.add(texture);
          setColourSpaces([texture]);
        },
        onChange: (index, texture, rect) => {
          const slot = index === 0 ? detailUniforms.uDetail0 : detailUniforms.uDetail1;
          const previous = slot.value;
          slot.value = texture;
          if (rect) (index === 0 ? detailUniforms.uDetailRect0 : detailUniforms.uDetailRect1).value.set(rect.lngMin, rect.latMax, rect.span);
          detailUniforms.uDetailOn.value.setComponent(index, texture ? 1 : 0);
          if (previous && previous !== texture) {
            colourTextures.current.delete(previous);
            previous.dispose();
          }
        },
      });
      const lastCamera = new Vector3();
      let cameraMovedAt = 0;
      let detailCheckedAt = 0;
      updateDetailRef.current = () => {
        const camera = globe.camera();
        const now = performance.now();
        if (camera.position.distanceToSquared(lastCamera) > 1e-6) {
          lastCamera.copy(camera.position);
          cameraMovedAt = now;
          return;
        }
        if (now - cameraMovedAt < 300 || now - detailCheckedAt < 300) return;
        detailCheckedAt = now;
        const { lat, lng } = globe.toGeoCoords(camera.position);
        // Ground per screen pixel at the view centre, in degrees.
        const altitude = camera.position.length() - globe.getGlobeRadius();
        const height = globe.renderer().getDrawingBufferSize(new THREE.Vector2()).y;
        const radians = (altitude / globe.getGlobeRadius()) * (2 * Math.tan((camera.fov * Math.PI) / 360)) / height;
        const baseDeg = 360 / (earthMaterialRef().map?.image?.width || 2048);
        detailUniforms.uBaseTexelDeg.value = baseDeg;
        detailPatches.update(lat, lng, (radians * 180) / Math.PI, baseDeg);

        // Grid level for this altitude; a zoomed-in region is rebuilt when
        // the view moves or zooms enough.
        const radius = globe.getGlobeRadius();
        const altitudeR = camera.position.length() / radius - 1;
        // The replay hides the news, so it keeps the global grid.
        const res = viewModeRef.current === "mission" ? HEX_RESOLUTION : gridLevelFor(altitudeR, gridRef.current.res);
        const key = res === HEX_RESOLUTION ? GLOBAL_GRID.key : regionKey(res, lat, lng, altitudeR);
        if (res === HEX_RESOLUTION) {
          if (gridRef.current.cells) setGrid(GLOBAL_GRID);
        } else if (key !== gridRef.current.key) {
          const region = regionCells({
            camera,
            radius,
            res,
            toGeo: (p) => globe.toGeoCoords(p),
            project: (cLat, cLng, dir) => {
              const p = globe.getCoords(cLat, cLng);
              dir.copy(p).normalize();
              return p.project(camera);
            },
          });
          setGrid({ res, cells: region.cells, cellSet: new Set(region.cells), key: region.key });
        }
      };
        },
        cancel: () => clearTimeout(seasonTimer),
      };
      showSeason(sceneTime(timeRef.current));

      const clouds = new THREE.Mesh(
        new THREE.SphereGeometry(
          globe.getGlobeRadius() * (1 + CLOUDS_ALT),
          75,
          75
        ),
        createCloudMaterial(new Vector3(0.0), cloudsTex, earthLighting.uniforms.uSunDir, earthLighting.uniforms)
      );
      clouds.rotation.y = -Math.PI / 2;
      globe.setCloudsMesh(clouds);
      cloudsMeshRef.current = clouds;
      clouds.visible = cloudsShownRef.current;
      earthLighting.uniforms.uCloudShadow.value = cloudsShownRef.current ? CLOUD_SHADOW : 0.0;
      globe.scene().add(clouds);

      // Unified Frame Callback:
      // 1. Dynamically adjust cloud altitude based on camera distance (Permanent)
      // 2. Track initial sun brightness until user interaction
      globe.setFrameCallback(() => {
        // Cloud Scaling (Permanent)
        const dist = globe.camera().position.length();
        const t = Math.min(1.0, Math.max(0.0, (dist - 150.0) / 400.0));
        // Capped below the atmosphere's top (0.0157 radii at true thickness).
        const dynamicAlt = CLOUDS_ALT + t * 0.012;
        clouds.scale.setScalar((1.0 + dynamicAlt) / (1.0 + CLOUDS_ALT));

        // Dynamic brightness tracking (Permanent)
        if (globe.sunLight) {
          const sunPos = globe.sunLight.position.clone().normalize();
          applyBrightness(globe);
          earthLighting.setSunDirection(sunPos);
          updateDetailRef.current?.();
          updateCalloutRef.current?.();
          issRef.current?.update(sceneTime(timeRef.current), sunPos);
          // Clouds fade out when zoomed in (with their shadows).
          const altitudeR = globe.camera().position.length() / globe.getGlobeRadius() - 1;
          const cloudFade = THREE.MathUtils.smoothstep(altitudeR, CLOUD_FADE_TO, CLOUD_FADE_FROM);
          const cloudsOn = cloudsShownRef.current && cloudFade > 0.001;
          clouds.visible = cloudsOn;
          if (clouds.material.userData.uZoomFade) clouds.material.userData.uZoomFade.value = cloudFade;
          earthLighting.uniforms.uCloudShadow.value = cloudsOn ? CLOUD_SHADOW * cloudFade : 0.0;
          // The clouds clear around the view onto the news; with the news off
          // (None) they ease back over it.
          const cloudWindow = earthLighting.uniforms.uCloudWindow;
          cloudWindow.value += ((newsShownRef.current ? globe.newsFade : 0) - cloudWindow.value) * 0.08;
          const sun = sunMeshRef.current;
          if (sun) {
            const moonMesh = moonMeshRef.current;
            const view = sunView(globe.camera(), sun.group.position, SUN_RADIUS, globe.getGlobeRadius(),
              moonMesh ? { position: moonMesh.position, radius: MOON_RADIUS } : null);
            sun.setTransmittance(view.transmittance);
            updateLensFlare(globe.flarePass, globe.camera(), sun.group.position, view, globe.exposure, globe.camera().aspect);
          } else {
            globe.flarePass.enabled = false;
          }
        }
      });

      const skybox = new THREE.Mesh(
        new THREE.SphereGeometry(globe.getGlobeRadius() * 40, 60, 60),
        new THREE.MeshBasicMaterial({
          map: starmapTex,
          side: THREE.BackSide,
          color: 0xffffff,
          transparent: true,
          depthWrite: false,
        })
      );
      skybox.renderOrder = -1;
      alignStarfield(skybox, initialTime);
      globe.skyboxMesh = skybox;
      globe.scene().add(skybox);

      // Keep brightness correct every frame until the user first interacts.
      // controls.change doesn't fire during the initial pointOfView tween, so
      // we need the animation-loop callback to track the moving camera.

      const atmos = createGlowMesh(
        new THREE.SphereGeometry(globe.getGlobeRadius() * 1.2, 75, 75),
        sunpos
      );
      atmosRef.current = atmos;
      globe.scene().add(atmos);

      // Physically based scattering at the limb (Rayleigh, Mie, ozone), on
      // larger screens only: it ray-marches 16x8 samples per pixel. Phones
      // keep just the cheaper glow above.
      if (!smallScreen) {
        const R = globe.getGlobeRadius();
        const scatteringMaterial = createAtmosphereMaterial({ sunPosition: sunpos.clone(), planetRadius: R, sunIntensity: SUN_INTENSITY });
        const scattering = new THREE.Mesh(
          new THREE.SphereGeometry(scatteringMaterial.uniforms.atmosphereRadius.value, 128, 128),
          scatteringMaterial
        );
        // After the clouds (inside the atmosphere, so they're dimmed too) and
        // before the news layer (renderOrder 1+), which stays undimmed.
        scattering.renderOrder = 0.5;
        scatteringRef.current = scattering;
        // The scattering shell draws the limb itself; the glow mesh is a
        // view-angle halo that also lit the night side, so it's only the
        // fallback (phones).
        atmos.visible = false;
        globe.scene().add(scattering);
      }

      // Moon — placed at the correct sub-lunar geographic point.
      // Pass the initial sun direction so the phase shader is correct from the start.
      const moonSub = getMoonSubPoint(initialTime);
      const moonDir = globe.getCoords(moonSub.lat, moonSub.lng, 0).normalize();
      const sunDir  = sunpos.clone().normalize();
      const { mesh: moonMesh, update: updateMoon, setSunDirection, setBrightness, upgradeTextures: upgradeMoon } = createMoonMesh(
        globe.scene(), loader, moonDir, sunDir, smallScreen
      );
      moonRef.current = { update: updateMoon, setSunDirection, setBrightness, upgradeTextures: upgradeMoon };
      earthLighting.setMoon(moonDir, sunDir);
      moonMeshRef.current = moonMesh;
      globe.addNewsOccluder(moonMesh);

      // Visible Sun disc + glow, in the same direction as the sunlight.
      sunMeshRef.current = createSunMesh(globe.scene(), sunpos.clone().normalize());

      // The ISS, propagated locally from CelesTrak's elements so it moves
      // every frame and follows the timeline (for the few days they're good).
      // Hidden until the setting turns it on.
      issRef.current = createIssMarker(globe);

      cameraReady.current = true;
      const openAt = (lat, lng) => {
        clearTimeout(openingFallback.current);
        openingViewDone.current = true;
        globe.pointOfView({ lat, lng, altitude: openingAltitude(globe.camera().aspect) }, 0);
        setOpeningReady(true);
      };
      openAtRef.current = openAt;
      if (openingSpot.current) {
        openAt(openingSpot.current.lat, openingSpot.current.lng);
      } else {
        openingFallback.current = setTimeout(() => {
          if (!openingViewDone.current) openAt(sunSub.lat, sunSub.lng);
        }, OPENING_WAIT_MS);
      }

      // The wireframe itself is built by the colour effect below.
      setWireframeReady(true);

      if (!smallScreen) {
        // 4k maps once the globe is up (the 2k ones keep the first load fast).
        // Each replaces every use of its 2k texture: the material slot and
        // any shader uniform sampling it.
        const highResLoader = new TextureLoader();
        const lighting = earthLighting.uniforms;
        const earthMaterial = globe.earthMesh.material;
        const upgrade = (path, uses, { colour = true } = {}) => highResLoader.load(path, (texture) => {
          if (globeEl.current !== globe) {
            texture.dispose();
            return;
          }
          texture.anisotropy = maxAnisotropy;
          const [[object, key]] = uses;
          const previous = object[key];
          if (colour) {
            colourTextures.current.delete(previous);
            colourTextures.current.add(texture);
            setColourSpaces([texture]);
          }
          for (const [target, slot] of uses) target[slot] = texture;
          for (const [target] of uses) if (target.isMaterial) target.needsUpdate = true;
          previous?.dispose();
        });
        seasonsRef.current?.upgrade();
        upgrade("/night4k.jpg", [[earthMaterial, "emissiveMap"], [lighting.uNight, "value"]]);
        upgrade("/roughness4k.jpg", [[earthMaterial, "roughnessMap"]], { colour: false });
        upgrade("/water4k.jpg", [[earthMaterial, "specularIntensityMap"], [lighting.uWater, "value"]], { colour: false });
        upgrade("/starmap4k.jpg", [[skybox.material, "map"]]);

        // Clouds follow the timeline: the API's Live map when live, otherwise
        // the selected hour's. The ground's cloud shadows follow them too.
        // Every swap cross-fades (uCloudsPrev → the new map over
        // CLOUD_FADE_MS), so Live ↔ scrubbing ↔ full maps don't pop.
        let fadeFrame = null;
        const cloudMaps = {
          live: cloudsTex,
          liveHour: null, // the API's Live hour, once its map is up
          show(tex, fadeMs = CLOUD_FADE_MS) {
            const prev = clouds.material.map;
            if (prev === tex) return;
            clouds.material.map = tex;
            lighting.uClouds.value = tex;
            lighting.uCloudsPrev.value = prev;
            lighting.uCloudsMix.value = 0;
            const start = performance.now();
            cancelAnimationFrame(fadeFrame);
            const step = () => {
              const t = Math.min(1, (performance.now() - start) / fadeMs);
              lighting.uCloudsMix.value = t * t * (3 - 2 * t);
              fadeFrame = t < 1 ? requestAnimationFrame(step) : null;
            };
            fadeFrame = requestAnimationFrame(step);
          },
          maps: createCloudMaps({
            load: (hour) => loadFullMap(cloudMapUrl(BACKEND_URL, hour), maxAnisotropy),
            release: (tex) => tex.dispose(),
          }),
        };
        // The last day scrubs seamlessly: its hourly frames are all fetched
        // up front and blended into this texture as the timeline moves.
        const scrubData = new Uint8Array(FRAME_WIDTH * FRAME_HEIGHT * 4);
        const scrub = cloudTexture(scrubData, FRAME_WIDTH, FRAME_HEIGHT, maxAnisotropy);
        cloudMaps.scrub = scrub;
        // Month-scale steps paint a day's frame into whichever of these isn't
        // up, so each step can fade from the last.
        const dayData = [0, 1].map(() => new Uint8Array(FRAME_WIDTH * FRAME_HEIGHT * 4));
        const dayTextures = dayData.map((data) => cloudTexture(data, FRAME_WIDTH, FRAME_HEIGHT, maxAnisotropy));
        cloudMaps.dayTextures = dayTextures;
        // Live: the newest NOAA hour at full resolution, kept ready by the
        // API, so the clouds at the Live end match the scrubbing frames.
        const showLive = (hour) => loadFullMap(cloudMapUrl(BACKEND_URL, hour), maxAnisotropy).then((tex) => {
          if (globeEl.current !== globe) {
            tex.dispose();
            return;
          }
          const old = cloudMaps.live;
          cloudMaps.live = tex;
          cloudMaps.liveHour = hour;
          if (timeRef.current == null) cloudMaps.show(tex);
          // The old map may still be fading out; let it finish first.
          if (old !== cloudsTex) setTimeout(() => old.dispose(), CLOUD_FADE_MS * 2);
        }).catch(() => {});
        cloudMaps.recent = createRecentClouds({
          list: () => fetchJsonWithTimeout(`${BACKEND_URL}/clouds_recent`),
          loadFrame: loadCloudFrame,
          onChange: () => setCloudFramesVersion((v) => v + 1),
          onLive: showLive,
        });
        // Paints `timeMs`'s blended frame and shows it; false when the frames
        // don't reach that far back.
        cloudMaps.paintRecent = (timeMs) => {
          if (cloudMaps.recent.paint(scrubData, timeMs)) {
            scrub.needsUpdate = true;
            cloudMaps.show(scrub);
            return true;
          }
          // Further back, a month-scale step: that midnight's frame.
          if (!cloudMaps.recent.hasDay(timeMs)) return false;
          const i = clouds.material.map === dayTextures[0] ? 1 : 0;
          cloudMaps.recent.paintDay(dayData[i], timeMs);
          dayTextures[i].needsUpdate = true;
          cloudMaps.show(dayTextures[i], CLOUD_DAY_STEP_FADE_MS);
          return true;
        };
        cloudMaps.recent.refresh().catch(() => {});
        recentCloudsTimer.current = setInterval(() => cloudMaps.recent.refresh().catch(() => {}), RECENT_CLOUDS_REFRESH_MS);
        cloudMapsRef.current = cloudMaps;
        // Until then, the matteason live map stands in.
        loadCloudMap(MATTEASON_CLOUDS_URL).then((tex) => {
          if (globeEl.current !== globe || cloudMaps.liveHour) {
            tex.dispose();
            return;
          }
          tex.anisotropy = maxAnisotropy;
          cloudMaps.live = tex;
          if (timeRef.current == null) cloudMaps.show(tex);
        }).catch(() => {});
      }

    }).catch(() => {
      setLoadError("The globe could not finish loading. You can still browse the story list.");
    }).finally(() => setIsLoading(false));

    return () => {
      clearTimeout(openingFallback.current);
      seasonsRef.current?.cancel();
      seasonsRef.current = null;
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointermove", onPointerMove);
      clearInterval(recentCloudsTimer.current);
      recentCloudsTimer.current = null;
      issRef.current?.dispose();
      issRef.current = null;
      cloudMapsRef.current?.maps.clear();
      cloudMapsRef.current?.scrub.dispose();
      cloudMapsRef.current?.dayTextures.forEach((tex) => tex.dispose());
      cloudMapsRef.current = null;
      globe.dispose();
      globeEl.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sun, Moon and stars at a moment — computed locally, so they move
  // instantly while scrubbing. `moonDir` overrides the Moon's direction (the
  // mission replay places it from the mission's own ephemeris).
  const applySky = (when, moonDirOverride = null) => {
    const g = globeEl.current;
    if (!g) return;
    seasonsRef.current?.show(when);
    const sunSub = getSunSubPoint(when);
    const sunpos = g.getCoords(sunSub.lat, sunSub.lng);
    g.sunLight.position.copy(sunpos);
    scatteringRef.current?.material.uniforms.sunPosition.value.copy(sunpos);
    if (atmosRef.current) {
      atmosRef.current.material.uniforms.sunpos.value.copy(sunpos);
    }
    if (g.skyboxMesh) alignStarfield(g.skyboxMesh, when);
    sunMeshRef.current?.update(sunpos.clone().normalize());
    if (moonRef.current) {
      const moonSub = moonDirOverride ? null : getMoonSubPoint(when);
      const moonDir = moonDirOverride ? moonDirOverride.clone() : g.getCoords(moonSub.lat, moonSub.lng, 0).normalize();
      moonRef.current.update(moonDir);
      moonRef.current.setSunDirection(sunpos.clone().normalize());
      earthLightingRef.current?.setMoon(moonDir, sunpos);
    }
  };
  const applySkyRef = useRef(applySky);
  applySkyRef.current = applySky;
  const missionRef = useRef(mission);
  missionRef.current = mission;

  // The sky follows the selected moment (the mission replay drives it while
  // it plays).
  useEffect(() => {
    if (!missionRef.current) applySky(sceneTime(time));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  // The Artemis II replay: the news fades away, the camera flies to the pad
  // and follows Orion, and the sky runs on mission time (missionClock, set by
  // App's mission timeline). Leaving it flies back and restores the news.
  useEffect(() => {
    const globe = globeEl.current;
    if (!globe || !mission) return;
    viewModeRef.current = "mission";
    callbacksRef.current.onFocus([]);
    collapseRef.current(true, false);
    hoverD.current = null;
    globe.setHudCursor(null, null);
    const fade = fadeNews(globe, 0, 600);
    moonRef.current?.upgradeTextures();

    const missionScene = createMissionScene(globe.scene(), mission);
    const state = {};
    const moonPos = new THREE.Vector3();
    let skyAt = null;
    const source = () => {
      const clock = missionClock?.current;
      const s = mission.stateAt(clock?.ms ?? mission.launchMs, state);
      if (s.ms !== skyAt) {
        applySkyRef.current(new Date(s.ms), s.moonDir);
        skyAt = s.ms;
      }
      missionScene.update(s, clock?.u ?? 0, globe.camera());
      moonPos.copy(s.moonDir).multiplyScalar(MOON_SCENE_DIST);
      // Up close the Moon fills the metered view: dim it as the moon view does.
      const far = THREE.MathUtils.smoothstep(globe.camera().position.distanceTo(moonPos), 80, 400);
      moonRef.current?.setBrightness(1.1 + (4.0 - 1.1) * far);
      return { target: s.position, frameAngle: s.frameAngle, framing: framingDistance(s.position, globe.getGlobeRadius(), moonPos, MOON_RADIUS) };
    };
    // Open on the pad from the south-west, a little above.
    const start = mission.stateAt(mission.launchMs);
    const up = start.position.clone().normalize();
    const east = new THREE.Vector3(0, 1, 0).cross(up).normalize();
    const north = new THREE.Vector3().crossVectors(up, east);
    const offset = up.clone().multiplyScalar(0.55).addScaledVector(east, -0.45).addScaledVector(north, -0.7).normalize().multiplyScalar(20);
    globe.beginFollow(source, offset, MISSION_INTRO_MS);

    return () => {
      fade.cancel();
      globe.endFollow(1500);
      missionScene.dispose();
      fadeNews(globe, 1, 900);
      moonRef.current?.setBrightness(4.0);
      viewModeRef.current = "earth";
      applySkyRef.current(sceneTime(timeRef.current));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission]);

  // Clouds follow the timeline. Within the last day they move with it,
  // blended from the prefetched hourly frames, and the month scale's steps
  // through the last month show each day's prefetched frame; once it rests,
  // the selected hour's full-resolution map replaces them (further back,
  // that map is all there is, and the previous clouds stay up until it
  // arrives).
  useEffect(() => {
    const cloudMaps = cloudMapsRef.current;
    if (!cloudMaps || !cloudsShown) return;
    const hour = cloudHour(time);
    if (hour == null) {
      cloudMaps.show(cloudMaps.live);
      return;
    }
    // A frame arriving later doesn't replace the full map it's resting on.
    if (cloudMaps.settledOn === time) return;
    cloudMaps.paintRecent(time);
  }, [time, cloudsShown, wireframeReady, cloudFramesVersion]);

  useEffect(() => {
    const cloudMaps = cloudMapsRef.current;
    const hour = cloudHour(time);
    if (!cloudMaps || !cloudsShown || hour == null) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      // The Live hour's full map is already up.
      const full = hour === cloudMaps.liveHour ? Promise.resolve(cloudMaps.live) : cloudMaps.maps.get(hour);
      full.then((tex) => {
        if (cancelled || cloudMapsRef.current !== cloudMaps) return;
        cloudMaps.settledOn = time;
        cloudMaps.show(tex);
      }, () => {});
    }, CLOUD_MAP_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [time, cloudsShown, wireframeReady]);

  // Articles in the rolling window ending at `time` (or the live feed).
  // Draws immediately from the timeline cache (compact month data) when
  // available, then fetches the full payload for the window's days once the
  // slider has stopped moving.
  useEffect(() => {
    let active = true;
    const windowKey = time == null ? "live" : new Date(time).toISOString();
    // Live is the same rolling 24 hours as any other moment, ending now, so
    // the globe doesn't change character when the slider leaves Live.
    const windowEnd = time ?? Date.now();
    const days = windowDays(windowEnd);
    const today = utcDayKey(Date.now());
    const current = () => timelineCache.getWindow(windowEnd);

    callbacksRef.current.onFocus([]);
    callbacksRef.current.onHover(false);
    lastOpened.current = null;
    hoverD.current = null;
    globeEl.current?.setHudCursor(null, null);

    const special = specialStoriesFor(windowEnd);
    const show = (entry) => {
      if (!active || (!entry && !special.length)) return;
      const points = special.length ? [...special, ...(entry?.points || [])] : entry.points;
      const cats = [...new Set(points.map(d => d.category).filter(Boolean))].sort();
      cats.forEach(cat => {
        if (!categoryColorsRef.current.has(cat)) {
          const idx = categoryColorsRef.current.size;
          categoryColorsRef.current.set(cat, CATEGORY_PALETTE[idx % CATEGORY_PALETTE.length]);
        }
      });
      // Union across days so the picker doesn't reshuffle while scrubbing.
      setCategories(prev => {
        const merged = [...new Set([...prev, ...cats])].sort();
        return merged.length === prev.length ? prev : merged;
      });
      setAllHexData(points);
      loaded.current = true;
      if (entry?.full && reportedData.current !== entry.points) {
        reportedData.current = entry.points;
        callbacksRef.current.onData(points, windowKey);
      }
    };

    show(current());
    timelineCache.requestMonths(monthsAround(time));
    const unsubscribe = timelineCache.subscribe((keys) => {
      if (days.some((d) => keys.has(d) || keys.has(`${d.slice(0, 7)}-*`))) show(current());
    });

    // Full payloads for any window day that lacks one. Today keeps growing,
    // so at Live it is refreshed even when cached.
    const missing = days.filter((d) => (time == null && d === today) || !timelineCache.getDay(d)?.full);
    const timer = missing.length ? setTimeout(() => {
      callbacksRef.current.onDataError(null);
      missing.forEach((day) => {
        fetchDayWithRetry(`${BACKEND_URL}/articles_date?pubDate=${day}`)
          .then((rawData) => timelineCache.setFullDay(day, toFullPoints(rawData)))
          .catch(() => {
            if (active) callbacksRef.current.onDataError("News could not be loaded. Try again.");
          });
      });
    }, isFirstDataLoad.current ? 0 : FULL_DAY_FETCH_DELAY_MS) : null;
    isFirstDataLoad.current = false;

    return () => {
      active = false;
      clearTimeout(timer);
      unsubscribe();
    };
  }, [time, retryKey]);

  useEffect(() => {
    if (!focusedStory) return;
    const spot = { lat: focusedStory.lat, lng: focusedStory.lng };
    // A ?story= link that resolves before the globe has opened becomes the
    // opening view; otherwise the opening view would override the flight.
    if (!openingViewDone.current) {
      openingSpot.current = spot;
      openingStoryPinned.current = true;
      if (cameraReady.current) openAtRef.current(spot.lat, spot.lng);
      return;
    }
    globeEl.current?.pointOfView(spot, 500);
  }, [focusedStory]);

  // Rebuild cell map whenever the full dataset or active category changes.
  useEffect(() => {
    if (allHexData.length === 0) {
      cellsById.current = new Map();
      kdTree.current = null;
      setPopData([]);
      return;
    }
    // One point per storyline at its current stage (see storylines.js).
    // With the news off there are no cells: the stories shrink away and
    // nothing can be picked.
    const filtered = collapseStorylines(allHexData.filter(d =>
      newsShown &&
      (!selectedCategory || d.category === selectedCategory) &&
      (!selectedCountry || d.country === selectedCountry)
    ), {
      endMs: time ?? Date.now(),
      eventMeta: (id) => timelineCache.getEventMeta(id),
      storylineEvents: (id) => timelineCache.getStorylineEvents(id),
    });
    kdTree.current = filtered.length ? getKDTree(filtered, grid.res) : null;
    let focusedId = hoverD.current?.id;
    const byId = new Map();
    // Importance fades with age, so fresh news stands tallest and
    // yesterday's settles down. A cell's freshness (its newest article's
    // recency) also shrinks its disc and glow, so a story fades away smoothly
    // as it leaves the window instead of dropping out.
    const windowEnd = time ?? Date.now();
    for (const p of filtered) {
      const id = latLngToCell(p.lat, p.lng, grid.res);
      let cell = byId.get(id);
      if (!cell) {
        cell = { cellId: id, points: [], weight: 0, maxImportance: 0, freshness: 0, newness: 0 };
        byId.set(id, cell);
      }
      const age = Number.isFinite(p.publishedAt) ? Math.max(0, windowEnd - p.publishedAt) : 0;
      const recency = recencyAt(age);
      cell.points.push(p);
      cell.weight += (p.val || 1) ** 2 * (p.boost || 1) * recency;
      cell.maxImportance = Math.max(cell.maxImportance, (p.importance || p.val || 1) * recency);
      cell.freshness = Math.max(cell.freshness, recency);
      cell.newness = Math.max(cell.newness, newnessAt(age));
    }
    cellsById.current = byId;
    if (!openingViewDone.current && !interacted.current && !openingStoryPinned.current) {
      const spot = mostImportantSpot(byId);
      if (spot) {
        openingSpot.current = spot;
        if (cameraReady.current) openAtRef.current(spot.lat, spot.lng);
      }
    }
    // Keep the open cell open with its new points: past days first draw from
    // compact data (no titles), and this is how its cards fill in once the
    // full articles arrive.
    // After a grid level change the open cell has another id: find it again
    // by position.
    if (focusedId && !byId.has(focusedId) && hoverD.current?.lat != null) {
      const moved = latLngToCell(hoverD.current.lat, hoverD.current.lng, grid.res);
      if (byId.has(moved)) {
        hoverD.current = { ...hoverD.current, id: moved };
        lastOpened.current = hoverD.current;
        focusedId = moved;
      }
    }
    const focused = focusedId && byId.get(focusedId);
    if (focused) {
      callbacksRef.current.onFocus(articlesOfPoints(focused.points));
    } else {
      if (!newsShown) {
        collapseRef.current(false, true);
        if (hoverD.current) globeEl.current?.setHexHighlight(null, null, true);
      }
      hoverD.current = null;
      lastOpened.current = null;
      callbacksRef.current.onFocus([]);
    }
    setPopData(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allHexData, selectedCategory, selectedCountry, time, grid, newsShown]);

  // Keep the renderer in sync if isMobile flips post-mount.
  useEffect(() => {
    const globe = globeEl.current;
    if (!globe) return;
    globe.setSize(globeWidth, dimensions.height);
  }, [globeWidth, dimensions.height]);


  // Wireframe grid: rebuilt only when its colour changes (it covers every
  // grid point on the globe, so it's far too heavy to rebuild per dataset).
  useEffect(() => {
    const globe = globeEl.current;
    if (!globe || !wireframeReady) return;
    const scale = cellScale(grid.res);
    globe.setHexWireframe({
      resolution: grid.res,
      cells: grid.cells,
      cellScale: scale,
      altitude: WIREFRAME_ALTITUDE,
      color: activeColor,
      useHeatmap: false,
      useElectric: false,
      opacity: WIREFRAME_OPACITY,
      rimBoost: WIREFRAME_RIM_BOOST,
      lineWidth: WIREFRAME_LINE_WIDTH,
      pointSize: WIREFRAME_POINT_SIZE * scale * dotSizeScale,
    });
  }, [activeColor, wireframeReady, grid]);

  // Data layer: the globe eases from the previous dataset to this one.
  useEffect(() => {
    const globe = globeEl.current;
    if (!globe || !wireframeReady) return;
    // Zoomed in: only the region's cells, with heights scaled to the finer
    // hexes so columns keep their proportions on screen.
    const { cellSet, res } = gridRef.current;
    const scale = cellScale(res);
    const rawAlts = new Map();
    const freshness = new Map();
    const newness = new Map();
    for (const [id, cell] of cellsById.current) {
      if (cellSet && !cellSet.has(id)) continue;
      rawAlts.set(id, prismAltitude(cell) * scale);
      freshness.set(id, cell.freshness);
      if (cell.newness > 0) newness.set(id, cell.newness);
    }
    globe.setDataCells(rawAlts, { smoothness: WIREFRAME_SMOOTHNESS, freshness, newness });
    // A new grid level drops the selection; keep the open place selected.
    const hover = hoverD.current;
    if (hover) globe.restoreSelection(globe.getCoords(hover.lat, hover.lng), hover.id);
  }, [popData, wireframeReady]);

  // Phones keep the categories and settings in a menu shelf.
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const pickCategory = (value) => {
    setNewsShown(true);
    onCategoryChange(value);
    setMenuOpen(false);
  };
  const pickNone = () => {
    setNewsShown(false);
    setMenuOpen(false);
  };
  const clearCountry = () => {
    onCountryChange("");
    setMenuOpen(false);
  };

  const categoryButtons = (
    <>
      {selectedCountry && (
        <button type="button" className="category-picker__item" onClick={clearCountry}>
          Clear {selectedCountry} filter
        </button>
      )}
      {[{ name: "All", color: "#4dd0ff", Icon: LanguageIcon }, { name: "None", Icon: DoNotDisturbAltIcon }, ...categories.map(cat => ({ name: cat, color: categoryColorsRef.current.get(cat), Icon: CATEGORY_ICONS[cat] || LanguageIcon }))].map(({ name, color, Icon }) => {
        const isActive = name === "None" ? !newsShown : newsShown && (name === "All" ? !selectedCategory : selectedCategory === name);
        return (
          <button
            key={name}
            type="button"
            className={`category-picker__item${isActive ? " is-active" : ""}`}
            aria-pressed={isActive}
            onClick={() => (name === "None" ? pickNone() : pickCategory(name === "All" ? "" : name))}
            title={name === "None" ? "Hide the news" : name}
            style={{ "--cat-color": color }}
          >
            <Icon />
            {name}
            {color && <span className="category-picker__swatch" aria-hidden="true" />}
          </button>
        );
      })}
    </>
  );
  const settingButtons = (
    <>
      <button
        type="button"
        className={`category-picker__item${snapEnabled ? " is-on" : ""}`}
        aria-pressed={snapEnabled}
        onClick={toggleSnap}
        title={snapEnabled ? "Disable camera snapping" : "Enable camera snapping"}
      >
        {snapEnabled ? <CenterFocusStrongIcon /> : <CenterFocusWeakIcon />}
        Snap
      </button>
      <button
        type="button"
        className={`category-picker__item${cloudsShown ? " is-on" : ""}`}
        aria-pressed={cloudsShown}
        onClick={toggleClouds}
        title={cloudsShown ? "Hide clouds" : "Show clouds"}
      >
        {cloudsShown ? <CloudIcon /> : <CloudOffIcon />}
        Clouds
      </button>
      <button
        type="button"
        className={`category-picker__item${issShown ? " is-on" : ""}`}
        aria-pressed={issShown}
        onClick={toggleIss}
        title={issShown ? "Hide the International Space Station" : "Show the International Space Station"}
      >
        <SatelliteAltIcon />
        ISS
      </button>
    </>
  );

  return (
    <div style={{ position: "relative", overflow: "hidden" }}>
      {loadError && (
        <div role="status" style={{ position: "absolute", top: 90, right: 16, zIndex: 1001, maxWidth: 320, padding: 12, color: "white", background: "#24394f", borderRadius: 8 }}>
          {loadError}
        </div>
      )}
      {(isLoading || !openingReady) && !loadError ? <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "absolute",
          paddingLeft: isMobile ? "0vw" : "15vw",
          zIndex: 100,
        }}
        className="solid"
      >
        <PuffLoader
          color={"#0D5299"}
          loading={true}
          size={"70vw"}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </div> : !loadError && <div className="fade" style={{ position: "absolute", inset: 0, zIndex: 100 }} aria-hidden="true" />}
      <div
        ref={containerRef}
        style={{ width: globeWidth, height: dimensions.height }}
      />
      {isMobile && (
        <HexCallout
          ref={calloutRef}
          articles={calloutArticles}
          expanded={expandedStories}
          onExpand={expandCallout}
          onCollapse={() => collapseCallout(true, true)}
          onOutbound={onOutbound}
          onWatchMission={onWatchMission}
        />
      )}
      {/* Category Picker UI: a column on desktop; on phones, in the menu shelf. */}
      {!isLoading && categories.length > 0 && !isMobile && !mission && (
        <div className="category-picker ui-panel" role="group" aria-label="Filter by category">
          {categoryButtons}
          <div className="category-picker__divider" />
          {settingButtons}
        </div>
      )}
      {isMobile && !isLoading && (
        <>
          <button
            type="button"
            className="menu-button ui-panel"
            onClick={() => setMenuOpen(true)}
            aria-expanded={menuOpen}
            aria-label={!newsShown ? "Menu, news off" : selectedCategory ? `Menu, showing ${selectedCategory}` : "Menu"}
          >
            {!newsShown && <span className="menu-button__filter menu-button__filter--off">News off</span>}
            {newsShown && selectedCategory && (
              <span className="menu-button__filter" style={{ "--cat-color": categoryColorsRef.current.get(selectedCategory) }}>
                {selectedCategory}
              </span>
            )}
            <MenuIcon />
          </button>
          <MenuShelf open={menuOpen} onClose={closeMenu}>
            {categories.length > 0 && (
              <>
                <h2 className="menu-shelf__heading">Categories</h2>
                <div role="group" aria-label="Filter by category">{categoryButtons}</div>
              </>
            )}
            <h2 className="menu-shelf__heading">Settings</h2>
            {settingButtons}
          </MenuShelf>
        </>
      )}
    </div>
  );
};

const MemoizedGlobeComponent = memo(GlobeComponent);
export default MemoizedGlobeComponent;
